import { asmx } from './http'
import { formatLocalDateTime, serializeLocalDateTime } from '../utils/date-time'

const normalizeAsmxDate = value => formatLocalDateTime(value, value)

const serializeTrainingRequest = request => Object.assign({}, request, {
  start_time: serializeLocalDateTime(request && request.start_time),
  deadline: serializeLocalDateTime(request && request.deadline)
})

const serializeLogQuery = request => Object.assign({}, request, {
  start_time: serializeLocalDateTime(request && request.start_time),
  end_time: serializeLocalDateTime(request && request.end_time)
})

const normalizeTrainingDetail = detail => {
  if (!detail) return detail
  ;['start_time', 'deadline', 'published_time', 'finished_time'].forEach(field => { detail[field] = normalizeAsmxDate(detail[field]) })
  ;(detail.participants || []).forEach(item => {
    ['first_view_time', 'last_view_time', 'complete_time', 'last_reminder_time'].forEach(field => { item[field] = normalizeAsmxDate(item[field]) })
  })
  return detail
}

export const authApi = {
  login: form => asmx('AuthService', 'Login', {
    login_name: form.login_name, password: form.password, client_type: 'PC'
  }, 'request'),
  profile: () => asmx('AuthService', 'GetProfile'),
  changePassword: form => asmx('AuthService', 'ChangeMyPassword', {
    old_password: form.old_password, new_password: form.new_password, confirm_password: form.confirm_password
  }, 'request'),
  logout: () => asmx('AuthService', 'Logout')
}

export const aiApi = {
  askComplianceAssistant: question => asmx('AiService', 'AskComplianceAssistant', { question }, 'request'),
  refreshDoctorProfile: doctorId => asmx('AiService', 'RefreshDoctorProfile', { doctor_id: doctorId }, 'request')
}

export const moduleApi = {
  dashboard: () => asmx('ModuleService', 'GetDashboard'),
  list: request => asmx('ModuleService', 'List', request, 'request'),
  get: (module, sjid) => asmx('ModuleService', 'Get', { module, sjid }, 'request'),
  save: request => asmx('ModuleService', 'Save', request, 'request'),
  changeStatus: request => asmx('ModuleService', 'ChangeStatus', request, 'request'),
  options: (optionType, parentId) => asmx('ModuleService', 'Options', {
    option_type: optionType, parent_id: parentId || null
  }, 'request')
}

export const visitApi = {
  filterOptions: () => asmx('ModuleService', 'GetVisitFilterOptions'),
  detail: sjid => asmx('VisitService', 'GetVisitDetail', { sjid }, 'request')
}

export const feedbackApi = {
  typeOptions: () => asmx('FeedbackService', 'GetFeedbackTypeOptions'),
  departmentOptions: () => asmx('FeedbackService', 'GetFeedbackCollaborationDepartmentOptions'),
  submitProcessing: request => asmx('FeedbackService', 'SubmitFeedbackProcessing', request, 'request'),
  statusSummary: () => asmx('FeedbackService', 'GetFeedbackStatusSummary'),
  detail: feedbackId => asmx('FeedbackService', 'GetFeedbackClosureDetail', { feedback_id: feedbackId }, 'request'),
  saveHandling: request => asmx('FeedbackService', 'SaveFeedbackHandling', request, 'request'),
  submitConfirmation: request => asmx('FeedbackService', 'SubmitFeedbackConfirmation', request, 'request')
}

export const terminalApi = {
  tree: keyword => asmx('TerminalService', 'GetTree', { keyword: keyword || '' }, 'request'),
  save: request => asmx('TerminalService', 'SaveTerminal', request, 'request')
}

export const doctorScheduleApi = {
  monthBaseTemplate: () => asmx('DoctorScheduleService', 'GetDoctorMonthBaseTemplate'),
  importMonthBase: request => asmx('DoctorScheduleService', 'ImportDoctorMonthBase', request, 'request'),
  week: request => asmx('DoctorScheduleService', 'GetDoctorScheduleWeek', request, 'request'),
  month: request => asmx('DoctorScheduleService', 'GetDoctorScheduleMonth', request, 'request'),
  detail: request => asmx('DoctorScheduleService', 'GetDoctorScheduleDetail', request, 'request'),
  options: () => asmx('DoctorScheduleService', 'GetScheduleOptions'),
  saveWeekly: request => asmx('DoctorScheduleService', 'SaveDoctorWeeklySchedule', request, 'request'),
  saveDay: request => asmx('DoctorScheduleService', 'SaveDoctorDaySchedule', request, 'request'),
  saveMonth: request => asmx('DoctorScheduleService', 'SaveDoctorMonthSchedule', request, 'request'),
  saveLeave: request => asmx('DoctorScheduleService', 'SaveDoctorLeave', request, 'request'),
  cancelDay: request => asmx('DoctorScheduleService', 'CancelDoctorDaySchedule', request, 'request')
}

export const trainingApi = {
  list: request => asmx('TrainingService', 'GetTrainingTaskList', request, 'request'),
  detail: taskSjid => asmx('TrainingService', 'GetTrainingTaskDetail', { task_sjid: taskSjid }, 'request').then(normalizeTrainingDetail),
  create: request => asmx('TrainingService', 'CreateTrainingTask', serializeTrainingRequest(request), 'request'),
  update: request => asmx('TrainingService', 'UpdateTrainingTask', serializeTrainingRequest(request), 'request'),
  publish: request => asmx('TrainingService', 'PublishTrainingTask', request, 'request'),
  preview: target => asmx('TrainingService', 'PreviewTrainingParticipants', target, 'request'),
  options: () => asmx('TrainingService', 'GetTrainingTargetOptions'),
  remind: request => asmx('TrainingService', 'RemindTrainingParticipants', request, 'request'),
  cancel: taskSjid => asmx('TrainingService', 'CancelTrainingTask', { task_sjid: taskSjid }, 'request'),
  confirmParticipant: request => asmx('TrainingService', 'ConfirmTrainingParticipant', request, 'request'),
  syncExamResults: taskSjid => asmx('TrainingService', 'SyncTrainingExamResults', { task_sjid: taskSjid }, 'request'),
  uploadMaterial: request => asmx('TrainingService', 'UploadTrainingMaterial', request, 'request')
}

export const productApi = {
  list: request => asmx('ProductService', 'List', request, 'request'),
  get: sjid => asmx('ProductService', 'Get', { sjid }, 'request'),
  regionOptions: () => asmx('ProductService', 'GetRegionOptions'),
  save: request => asmx('ProductService', 'Save', pick(request, ['sjid','product_code','product_name','generic_name','approval_no','dosage_form','specification','therapeutic_area','promotion_status','promotion_region','promotion_scope_type','promotion_region_ids','indication','adverse_reaction','contraindication','status']), 'request'),
  ask: request => asmx('AiService', 'AskProductKnowledge', {
    question: request.question,
    product_sjid: request.product_sjid || null
  }, 'request')
}

const pick = (source, fields) => fields.reduce((target, field) => {
  target[field] = source[field] === undefined ? null : source[field]
  return target
}, {})

const personnelFields = ['sjid','user_code','login_name','display_name','mobile','region_id','group_id','position_name','manager_user_id','education_level','major_name','employment_status','ai_reminder_enabled','role_id','filing_no','filing_status','filing_valid_from','filing_valid_to','filing_scope','status']
const regionFields = ['sjid','parent_id','region_code','region_name','region_level','manager_user_id','status']
const regionGroupFields = ['sjid','group_code','group_name','manager_user_id','region_ids','status','sort_no']

export const organizationApi = {
  listPersonnel: request => asmx('OrganizationService', 'ListPersonnel', request, 'request'),
  getPersonnel: sjid => asmx('OrganizationService', 'GetPersonnel', { sjid }, 'request'),
  changePersonnelPassword: request => asmx('OrganizationService', 'ChangePersonnelPassword', pick(request, ['sjid', 'new_password']), 'request'),
  savePersonnel: request => {
    const payload = pick(request, personnelFields)
    payload.filing_valid_from = payload.filing_valid_from || null
    payload.filing_valid_to = payload.filing_valid_to || null
    return asmx('OrganizationService', 'SavePersonnel', payload, 'request')
  },
  listRegions: () => asmx('OrganizationService', 'ListRegions'),
  saveRegion: request => asmx('OrganizationService', 'SaveRegion', pick(request, regionFields), 'request'),
  listRegionGroups: () => asmx('OrganizationService', 'ListRegionGroups'),
  saveRegionGroup: request => asmx('OrganizationService', 'SaveRegionGroup', pick(request, regionGroupFields), 'request'),
  options: () => asmx('OrganizationService', 'GetOptions')
}

export const roleApi = {
  list: () => asmx('RoleService', 'ListRoles'),
  get: sjid => asmx('RoleService', 'GetRole', { sjid }, 'request'),
  permissions: () => asmx('RoleService', 'GetPermissions'),
  save: request => asmx('RoleService', 'SaveRole', pick(request, ['sjid','role_code','role_name','data_scope','status','permission_ids']), 'request')
}

export const logApi = {
  getSummary: () => asmx('LogService', 'GetSummary'),
  getSystemLogs: request => asmx('LogService', 'GetSystemLogs', serializeLogQuery(request), 'request'),
  getSystemLogDetail: sjid => asmx('LogService', 'GetSystemLogDetail', { sjid }, 'request'),
  getAiLogs: request => asmx('LogService', 'GetAiLogs', serializeLogQuery(request), 'request'),
  getAiLogDetail: sjid => asmx('LogService', 'GetAiLogDetail', { sjid }, 'request'),
  getTaskLogs: request => asmx('LogService', 'GetTaskLogs', serializeLogQuery(request), 'request'),
  getTaskLogDetail: sjid => asmx('LogService', 'GetTaskLogDetail', { sjid }, 'request')
}
